import { checkDevice, deviceFetch, enrollTablet, postJson, unlockDriver } from '../api';
import { LANGUAGES, type LanguageCode } from '../i18n';
import { APP_VERSION, config, installId, resetHistory, saveRoute, state } from '../state';
import { recognitionCtor } from '../speech';
import { $, escapeHtml, need, show, toast } from '../ui/modal';
import { loadPublicConfig } from '../api';
import { kioskReset } from '../kiosk';

// ---------------------------------------------------------------------------
// Puerta de entrada: alta de tablet o PIN de conductor
// ---------------------------------------------------------------------------

export async function driverGate(): Promise<void> {
  let authorized = false;
  try {
    authorized = (await checkDevice()).authorized === true;
  } catch { /* sin red: se ofrece el alta igualmente */ }

  if (!authorized) {
    show('Autorizar tablet', `
      <form id="enrollForm" class="authbox">
        <p>Esta tablet todavía no pertenece a ningún taxi. Genera un <b>código de alta</b> desde
           Talkxi Fleet Control. El código es de un solo uso y caduca en 15 minutos.</p>
        <label class="fieldlabel">Código de alta Fleet
          <input id="fleetEnroll" maxlength="16" autocomplete="one-time-code"
                 autocapitalize="characters" placeholder="Ej. A7K9P2XM4Q" required>
        </label>
        <button class="cta">Vincular tablet</button>
        <p id="pinError" class="fineprint" style="color:#9a2929"></p>
      </form>`);

    need<HTMLInputElement>('#fleetEnroll').focus();
    need<HTMLFormElement>('#enrollForm').onsubmit = async (event) => {
      event.preventDefault();
      const button = need<HTMLButtonElement>('#enrollForm button.cta');
      button.disabled = true;
      button.textContent = '…';
      try {
        const result = await enrollTablet(need<HTMLInputElement>('#fleetEnroll').value.trim().toUpperCase());
        if (result.scope?.vehicle_id) config.vehicleId = result.scope.vehicle_id;
        if (result.scope?.tenant_id) config.tenantId = result.scope.tenant_id;
        await loadPublicConfig();
        toast('Tablet vinculada');
        void driverGate();
      } catch (err) {
        need('#pinError').textContent = err instanceof Error ? err.message : 'No se pudo autorizar';
        button.disabled = false;
        button.textContent = 'Vincular tablet';
      }
    };
    return;
  }

  show('Modo conductor · PIN', `
    <form id="pinForm" class="authbox">
      <p>La tablet ya está autorizada. Introduce el <b>PIN de conductor de esta empresa</b>
         para abrir los ajustes locales.</p>
      <input id="driverPin" inputmode="numeric" pattern="[0-9]*" minlength="6" maxlength="8"
             autocomplete="off" placeholder="PIN de 6–8 dígitos" required>
      <button class="cta">Abrir Modo conductor</button>
      <p class="fineprint">Este PIN no sirve para añadir tablets nuevas y no sustituye la contraseña de Fleet Control.</p>
      <p id="pinError" class="fineprint" style="color:#9a2929"></p>
    </form>`);

  need<HTMLInputElement>('#driverPin').focus();
  need<HTMLFormElement>('#pinForm').onsubmit = async (event) => {
    event.preventDefault();
    const button = need<HTMLButtonElement>('#pinForm button.cta');
    button.disabled = true;
    button.textContent = '…';
    try {
      await unlockDriver(need<HTMLInputElement>('#driverPin').value.trim());
      driverMode();
    } catch (err) {
      need('#pinError').textContent = err instanceof Error ? err.message : 'PIN incorrecto';
      button.disabled = false;
      button.textContent = 'Abrir Modo conductor';
    }
  };
}

// ---------------------------------------------------------------------------
// Pantalla y energía
// ---------------------------------------------------------------------------

export async function requestWakeLock(): Promise<void> {
  try {
    if (!('wakeLock' in navigator)) { toast('Wake Lock no disponible'); return; }
    state.wakeLock = await navigator.wakeLock.request('screen');
    localStorage.setItem('t2aKeepAwake', '1');
    state.wakeLock.addEventListener('release', () => { state.wakeLock = null; });
    toast('Pantalla activa');
  } catch {
    toast('Wake Lock no disponible');
  }
}

async function requestFullscreen(): Promise<void> {
  try {
    await document.documentElement.requestFullscreen();
    toast('Pantalla completa');
  } catch {
    toast('Pantalla completa no disponible');
  }
}

// ---------------------------------------------------------------------------
// Métricas piloto
// ---------------------------------------------------------------------------

const METRIC_LABELS: Record<string, string> = {
  session_started: 'Sesiones pasajero',
  chat_open: 'Aperturas de Talkxi',
  chat_sent: 'Preguntas enviadas',
  chat_ok: 'Respuestas correctas',
  chat_error: 'Errores de chat',
  interpreter_open: 'Aperturas intérprete',
  interpreter_voice_start: 'Usos de micrófono',
  interpreter_ok: 'Traducciones correctas',
  interpreter_error: 'Errores intérprete',
  booking_open: 'Reservas abiertas',
  booking_whatsapp: 'Reservas enviadas a WhatsApp',
  booking_ai_cta: 'Reservas sugeridas por Talkxi',
  reviews_open: 'QR reseñas abierto',
  source_open: 'Fuentes abiertas',
  privacy_open: 'Privacidad consultada',
};

interface MetricsSummary {
  total_sessions: number;
  retention_days: number;
  totals: Record<string, number>;
  languages: Record<string, number>;
  days: Array<{ date: string; sessions: number }>;
}

async function pilotMetrics(): Promise<void> {
  show('Métricas piloto', `
    <div id="metricsBody"><p>Cargando métricas agregadas…</p></div>
    <div class="driver-actions"><button id="metricsBack" class="mini" type="button">Volver al modo conductor</button></div>`);
  need('#metricsBack').onclick = driverMode;

  const target = need('#metricsBody');
  try {
    const { ok, data } = await postJson<{ summary?: MetricsSummary }>('/api/metrics.php', { action: 'summary' }, 15_000);
    if (!ok || !data.summary) {
      target.innerHTML = '<p class="fineprint" style="color:#9a2929">No se pudieron leer las métricas.</p>';
      return;
    }
    const s = data.summary;

    const eventRows = Object.entries(METRIC_LABELS).map(([key, label]) =>
      `<div class="metric-row"><span>${label}</span><strong>${Number(s.totals?.[key] ?? 0)}</strong></div>`).join('');

    const langRows = Object.entries(s.languages ?? {})
      .sort((a, b) => b[1] - a[1])
      .map(([code, count]) => {
        const flag = LANGUAGES[code as LanguageCode]?.flag ?? '';
        return `<span class="metric-lang">${flag} ${code.toUpperCase()}: <b>${count}</b></span>`;
      }).join(' ');

    const days = (s.days ?? [])
      .map((d) => `<div class="metric-day"><span>${escapeHtml(d.date)}</span><b>${Number(d.sessions ?? 0)} sesiones</b></div>`)
      .join('') || '<p class="fineprint">Todavía no hay sesiones registradas.</p>';

    target.innerHTML = `
      <div class="metric-summary">
        <div><b>${Number(s.total_sessions ?? 0)}</b><small>sesiones totales</small></div>
        <div><b>${Number(s.retention_days ?? 45)}</b><small>días de retención</small></div>
      </div>
      <h3>Uso</h3>${eventRows}
      <h3>Idiomas de sesión</h3><p>${langRows || 'Sin datos todavía.'}</p>
      <h3>Últimos días</h3>${days}
      <p class="fineprint">Solo contadores agregados. No se almacenan preguntas, traducciones,
         nombres, teléfonos ni direcciones en estas métricas.</p>`;
  } catch {
    target.innerHTML = '<p class="fineprint" style="color:#9a2929">Métricas no disponibles.</p>';
  }
}

// ---------------------------------------------------------------------------
// Diagnóstico
// ---------------------------------------------------------------------------

interface SystemStatus {
  tenant_id?: string; vehicle_id?: string; openai_configured?: boolean;
  model?: string; web_search_enabled?: boolean; metrics_storage_ready?: boolean; https?: boolean;
}

async function systemDiagnostics(): Promise<void> {
  show('Diagnóstico Talkxi', `
    <div id="diagBody"><p>Comprobando sistema…</p></div>
    <div class="driver-actions"><button id="diagBack" class="mini" type="button">Volver al modo conductor</button></div>`);
  need('#diagBack').onclick = driverMode;

  const target = need('#diagBody');
  const browser = {
    online: navigator.onLine,
    mic: Boolean(recognitionCtor()),
    voice: 'speechSynthesis' in window,
    serviceWorker: 'serviceWorker' in navigator,
    wakeLock: 'wakeLock' in navigator,
  };

  try {
    const res = await deviceFetch('/api/system.php', { method: 'POST', body: JSON.stringify({ action: 'status' }) }, 12_000);
    const d = (await res.json()) as SystemStatus;
    const rows: Array<[string, string]> = [
      ['Versión', `Talkxi ${APP_VERSION}`],
      ['Dominio', config.publicDomain || '—'],
      ['Operador', config.tenantName || '—'],
      ['Tenant', d.tenant_id || config.tenantId || '—'],
      ['Vehículo', d.vehicle_id || config.vehicleId || '—'],
      ['Instalación', installId()],
      ['Internet', browser.online ? 'OK' : 'Sin conexión'],
      ['Tablet autorizada', res.ok ? 'OK' : 'No'],
      ['OpenAI', d.openai_configured ? 'Configurado' : 'Falta API key'],
      ['Modelo', d.model || '—'],
      ['Búsqueda web servidor', d.web_search_enabled ? 'Activa' : 'Desactivada'],
      ['Métricas', d.metrics_storage_ready ? 'Listas' : 'Revisar permisos'],
      ['HTTPS', d.https ? 'OK' : 'Requerido'],
      ['Micrófono navegador', browser.mic ? 'Disponible' : 'No disponible'],
      ['Voz navegador', browser.voice ? 'Disponible' : 'No disponible'],
      ['Service Worker', browser.serviceWorker ? 'Disponible' : 'No disponible'],
      ['Wake Lock', browser.wakeLock ? 'Disponible' : 'No disponible'],
    ];
    target.innerHTML = `<div class="diag-grid">${
      rows.map(([k, v]) => `<div class="diag-row"><span>${escapeHtml(k)}</span><strong>${escapeHtml(v)}</strong></div>`).join('')
    }</div><p class="fineprint">Este diagnóstico no realiza una llamada de pago a la IA.
      Solo comprueba configuración y capacidades de la tablet.</p>`;
  } catch {
    target.innerHTML = `<p class="fineprint" style="color:#9a2929">No se pudo completar el diagnóstico.
      Comprueba conexión y autorización de la tablet.</p>`;
  }
}

// ---------------------------------------------------------------------------
// Panel del conductor
// ---------------------------------------------------------------------------

export function driverMode(): void {
  const presetFrom = String(config.defaultRouteFrom ?? '').trim();
  const presetTo = String(config.defaultRouteTo ?? '').trim();
  const hasPreset = Boolean(presetFrom && presetTo);
  const current = hasPreset ? (state.route.key || 'configured') : 'other';

  const presets = hasPreset ? `
    <label><input type="radio" name="routeMode" value="configured" ${current === 'configured' ? 'checked' : ''}>
      🚕 ${escapeHtml(presetFrom)} → ${escapeHtml(presetTo)}</label>
    <label><input type="radio" name="routeMode" value="reverse" ${current === 'reverse' ? 'checked' : ''}>
      ↩️ ${escapeHtml(presetTo)} → ${escapeHtml(presetFrom)}</label>` : '';

  show('Modo conductor', `
    <div class="driver-panel">
      <div><span class="status-pill" id="authStatus">Tablet autorizada</span></div>
      <p><strong>Trayecto que Talkxi usará como contexto:</strong></p>
      ${presets}
      <label><input type="radio" name="routeMode" value="other" ${current === 'other' ? 'checked' : ''}> 🚕 Otro trayecto</label>
      <div id="customRoute" class="fields ${current === 'other' ? '' : 'hidden'}">
        <input id="routeFrom" value="${escapeHtml(current === 'other' ? state.route.from : '')}" placeholder="Origen">
        <input id="routeTo" value="${escapeHtml(current === 'other' ? state.route.to : '')}" placeholder="Destino">
      </div>
      <div class="settings-grid">
        <label class="toggle-row">Voz automática <input id="autoVoice" type="checkbox" ${state.autoVoice ? 'checked' : ''}></label>
        <label class="toggle-row">Búsqueda web <input id="webSearch" type="checkbox" ${state.webSearch ? 'checked' : ''}></label>
      </div>
      <label class="fieldlabel">Volumen de voz
        <input id="voiceVolume" type="range" min="0" max="1" step="0.05" value="${state.voiceVolume}">
      </label>
      <button id="saveDriver" class="cta" type="button">Guardar ajustes</button>
      <div class="driver-actions">
        <button id="fullscreenBtn" class="mini" type="button">Pantalla completa</button>
        <button id="wakeBtn" class="mini" type="button">Mantener pantalla activa</button>
        <button id="clearDriver" class="mini" type="button">Borrar conversación</button>
        <button id="metricsBtn" class="mini" type="button">Métricas piloto</button>
        <button id="fleetBtn" class="mini" type="button">Fleet Control</button>
        <button id="diagBtn" class="mini" type="button">Diagnóstico Talkxi</button>
        <button id="resetPassenger" class="mini danger" type="button">Nueva sesión pasajero</button>
      </div>
      <p class="fineprint">La conversación y el idioma del pasajero se reinician automáticamente tras
         3 minutos sin actividad. El trayecto y los ajustes del conductor permanecen guardados.</p>
    </div>`);

  for (const radio of document.querySelectorAll<HTMLInputElement>('input[name="routeMode"]')) {
    radio.onchange = () => $('#customRoute')?.classList.toggle('hidden', radio.value !== 'other');
  }

  need('#saveDriver').onclick = () => {
    const selected = document.querySelector<HTMLInputElement>('input[name="routeMode"]:checked')?.value ?? 'other';
    if (selected === 'configured') saveRoute({ key: 'configured', from: presetFrom, to: presetTo });
    else if (selected === 'reverse') saveRoute({ key: 'reverse', from: presetTo, to: presetFrom });
    else {
      saveRoute({
        key: 'other',
        from: need<HTMLInputElement>('#routeFrom').value.trim().slice(0, 120),
        to: need<HTMLInputElement>('#routeTo').value.trim().slice(0, 120),
      });
    }
    state.autoVoice = need<HTMLInputElement>('#autoVoice').checked;
    state.webSearch = need<HTMLInputElement>('#webSearch').checked;
    state.voiceVolume = Number(need<HTMLInputElement>('#voiceVolume').value);
    localStorage.setItem('t2aAutoVoice', state.autoVoice ? '1' : '0');
    localStorage.setItem('t2aWebSearch', state.webSearch ? '1' : '0');
    localStorage.setItem('t2aVoiceVolume', String(state.voiceVolume));
    toast('Ajustes guardados');
    kioskReset(false);
  };

  need('#fullscreenBtn').onclick = () => void requestFullscreen();
  need('#wakeBtn').onclick = () => void requestWakeLock();
  need('#clearDriver').onclick = () => { resetHistory(); toast('Conversación borrada'); };
  need('#metricsBtn').onclick = () => void pilotMetrics();
  need('#fleetBtn').onclick = () => { location.href = '/fleet/'; };
  need('#diagBtn').onclick = () => void systemDiagnostics();
  need('#resetPassenger').onclick = () => kioskReset(true);
}

/** Pulsación larga de 2,8 s sobre el logo: entrada oculta al Modo conductor. */
export function setupDriverLongPress(): void {
  const zone = $('#driverZone');
  if (!zone) return;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const start = (event: PointerEvent) => {
    if (event.cancelable) event.preventDefault();
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => { timer = null; void driverGate(); }, 2800);
  };
  const cancel = () => { if (timer) { clearTimeout(timer); timer = null; } };

  zone.addEventListener('pointerdown', start);
  zone.addEventListener('pointerup', cancel);
  zone.addEventListener('pointercancel', cancel);
  zone.addEventListener('pointerleave', cancel);
  zone.addEventListener('contextmenu', (e) => e.preventDefault());
}
